import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    IconButton,
    Input,
    Select,
    Stack,
    Switch,
    Text,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import { Select as MultiSelect } from 'chakra-react-select';
import { useMemo, useState } from 'react';
import { MdDelete, MdEdit } from 'react-icons/md';
import ConfirmDialog from '../../../components/ConfirmDialog';
import InlineErrorState from '../../../components/states/InlineErrorState';
import useErrorToast from '../../../hooks/useErrorToast';
import { translate } from '../../../i18n';
import {
    ALLOWED_COMMITMENT_MONTHS,
    CreateMembershipVariantDto,
    MembershipVariantDto,
    MembershipVariantPaymentMode,
    UserMembershipTypeDto,
} from '../../../models/user-membership/dto/user-membership.dto';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { useCreateMembershipVariant } from '../hooks/useCreateMembershipVariant';
import { useDeleteMembershipVariant } from '../hooks/useDeleteMembershipVariant';
import { useGetUserMembershipTypes } from '../hooks/useGetUserMembershipTypes';
import { useUpdateMembershipVariant } from '../hooks/useUpdateMembershipVariant';
import { isUnreadable } from '../../analytics/utils/statValue';

type Props = {
    membershipType: UserMembershipTypeDto;
};

type Draft = CreateMembershipVariantDto;

const NEW_VARIANT_DRAFT = (currency: string | undefined): Draft => ({
    label: '',
    priceInCents: 0,
    currency: currency ?? 'eur',
    paymentMode: MembershipVariantPaymentMode.RECURRING,
    commitmentMonths: 12,
    isDefault: false,
    isDuoBeneficiary: false,
    duoPartnerTypeKeys: [],
});

const centsToEuros = (cents: number | undefined): string => {
    if (cents === undefined || cents === null) return '';
    return (cents / 100).toFixed(2);
};

const eurosToCents = (value: string): number => {
    if (!value) return 0;
    const parsed = parseFloat(value.replace(',', '.'));
    if (isNaN(parsed)) return 0;
    return Math.round(parsed * 100);
};

const formatVariantPrice = (variant: MembershipVariantDto): string => {
    const symbol = variant.currency === 'usd' ? '$' : variant.currency === 'gbp' ? '£' : '€';
    return `${symbol}${(variant.priceInCents / 100).toFixed(2)}`;
};

/**
 * MYS-125 P2.1 — variant CRUD. Mirrors MembershipTypeCguOverrideSection's
 * shape: an edit-mode-only section (a variant needs a real :typeId to POST
 * against) with its own hooks hitting dedicated endpoints, isolated from the
 * generic AddUserMembershipTypeModal submit path.
 *
 * D6 (isDuoBeneficiary => priceInCents === 0) and D5 (commitmentMonths in
 * ALLOWED_COMMITMENT_MONTHS) are hard-validated server-side
 * (assertMembershipVariantFields) — this form mirrors those checks
 * client-side only for a legible inline error, never as the source of truth.
 */
export default function MembershipVariantsSection({ membershipType }: Props) {
    const toast = useToast();
    const showError = useErrorToast();
    const subtleColor = useColorModeValue('secondaryGray.600', 'secondaryGray.400');

    const orgId = membershipType.organizationId;
    const typeId = membershipType.id;
    const variants = membershipType.variants ?? [];

    const { mutateAsync: createVariant, isLoading: isCreating } = useCreateMembershipVariant(orgId, typeId);
    const { mutateAsync: updateVariant, isLoading: isUpdating } = useUpdateMembershipVariant(orgId, typeId);
    const { mutateAsync: deleteVariant, isLoading: isDeleting } = useDeleteMembershipVariant(orgId, typeId);

    // MYS-125 P4.9 — D6's resolution key is (variant, partner's parent
    // TYPE), and `resolvePartnerParentTypeKey` resolves that key from
    // `UserMembershipType.name` (api/.../resolve-partner-parent-type-key.ts),
    // not an id — so the options here are every live type NAME in this org,
    // including this type's own (a Duo pair can share one type). No filter
    // by isSubscriptionBased/etc: the resolver doesn't discriminate either.
    //
    // Deduped because `UserMembershipType.name` carries no unique constraint:
    // two same-named types would otherwise render as two indistinguishable
    // options sharing one value. They also resolve identically server-side
    // (the key IS the name), so collapsing them loses nothing.
    const { data: orgMembershipTypes, isError: isMembershipTypesError } =
        useGetUserMembershipTypes(orgId);
    const partnerTypeOptions = useMemo(
        () =>
            Array.from(new Set((orgMembershipTypes ?? []).map((t) => t.name)))
                .map((name) => ({ label: name, value: name })),
        [orgMembershipTypes],
    );

    // Which row is being edited: a real variant id, the sentinel 'new' for the
    // add-row, or null when the list is read-only.
    const [editingId, setEditingId] = useState<string | 'new' | null>(null);
    const [draft, setDraft] = useState<Draft>(NEW_VARIANT_DRAFT(membershipType.currency));
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const startEdit = (variant: MembershipVariantDto) => {
        setDraft({
            label: variant.label,
            priceInCents: variant.priceInCents,
            currency: variant.currency,
            paymentMode: variant.paymentMode,
            commitmentMonths: variant.commitmentMonths,
            isDefault: variant.isDefault,
            isDuoBeneficiary: variant.isDuoBeneficiary,
            duoPartnerTypeKeys: variant.duoPartnerTypeKeys ?? [],
        });
        setEditingId(variant.id);
    };

    const startAdd = () => {
        setDraft(NEW_VARIANT_DRAFT(membershipType.currency));
        setEditingId('new');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft(NEW_VARIANT_DRAFT(membershipType.currency));
    };

    const setDraftField = <K extends keyof Draft>(key: K, value: Draft[K]) => {
        setDraft((prev) => {
            const next = { ...prev, [key]: value };
            // D6: forcing this on client-side mirrors the server's hard
            // rejection rather than letting the admin submit a mismatched
            // price+flag pair the Duo resolver will never actually charge.
            if (key === 'isDuoBeneficiary' && value) {
                next.priceInCents = 0;
                // MYS-125 P4.9 — the same rejection covers duoPartnerTypeKeys
                // (assertMembershipVariantFields): the 0€ beneficiary half
                // doesn't itself resolve to a partner type, it's what a
                // partner-type match grants. Clearing here keeps the two
                // switches mutually exclusive in the UI, not just at submit.
                next.duoPartnerTypeKeys = [];
            }
            return next;
        });
    };

    // Mirrors assertMembershipVariantFields (api/src/user-membership/service/
    // membership-variant/assert-membership-variant.ts) — same checks, purely
    // for an inline error; the service is the real gate.
    const labelError = draft.label.trim().length === 0
        ? translate('userMembership.variant_invalid_label')
        : undefined;
    const priceError = !Number.isInteger(draft.priceInCents) || draft.priceInCents < 0
        ? translate('userMembership.variant_invalid_price')
        : undefined;
    const currencyError = !/^[a-z]{3}$/.test(draft.currency)
        ? translate('userMembership.variant_invalid_currency')
        : undefined;
    const commitmentError = !(ALLOWED_COMMITMENT_MONTHS as readonly number[]).includes(draft.commitmentMonths)
        ? translate('userMembership.variant_invalid_commitment')
        : undefined;
    const duoBeneficiaryPriceError = draft.isDuoBeneficiary && draft.priceInCents !== 0
        ? translate('userMembership.variant_invalid_duo_beneficiary_price')
        : undefined;
    // Defense-in-depth, same as duoBeneficiaryPriceError above: setDraftField
    // already clears duoPartnerTypeKeys the moment isDuoBeneficiary is
    // toggled on, so this can't normally be reached through the form — but a
    // row hydrated from data written before this validation existed could
    // still carry both, and Save must not silently re-submit that mismatch.
    const duoPartnerTypeKeysError = draft.isDuoBeneficiary && (draft.duoPartnerTypeKeys?.length ?? 0) > 0
        ? translate('userMembership.variant_invalid_duo_partner_type_keys')
        : undefined;
    const isDraftValid = !labelError && !priceError && !currencyError && !commitmentError
        && !duoBeneficiaryPriceError && !duoPartnerTypeKeysError;

    const isSaving = isCreating || isUpdating;

    const handleSave = async () => {
        if (!isDraftValid) return;
        try {
            if (editingId === 'new') {
                await createVariant(draft);
            } else if (editingId) {
                await updateVariant({ variantId: editingId, body: draft });
            }
            cancelEdit();
            toast({ title: translate('common.save'), status: 'success', duration: 2000, position: 'top-right' });
        } catch (err) {
            showError(err);
        }
    };

    const handleDelete = async () => {
        if (!pendingDeleteId) return;
        try {
            await deleteVariant(pendingDeleteId);
        } catch (err) {
            showError(err);
        } finally {
            setPendingDeleteId(null);
        }
    };

    const renderDraftRow = () => (
        <Box borderWidth="1px" borderRadius="md" p={3} mb={2}>
            <Flex gap={3} flexWrap="wrap" alignItems="flex-start">
                <FormControl flex="2" minW="140px" isInvalid={Boolean(labelError)}>
                    <FormLabel fontSize="xs">{translate('userMembership.variant_label')}</FormLabel>
                    <Input
                        size="sm"
                        placeholder={translate('userMembership.variant_label_placeholder')}
                        value={draft.label}
                        onChange={(e) => setDraftField('label', e.target.value)}
                    />
                    {labelError && <FormErrorMessage>{labelError}</FormErrorMessage>}
                </FormControl>

                <FormControl flex="1" minW="100px" isInvalid={Boolean(priceError) || Boolean(duoBeneficiaryPriceError)}>
                    <FormLabel fontSize="xs">{translate('userMembership.variant_price')}</FormLabel>
                    <Input
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        value={centsToEuros(draft.priceInCents)}
                        onChange={(e) => setDraftField('priceInCents', eurosToCents(e.target.value))}
                        isDisabled={draft.isDuoBeneficiary}
                    />
                    {priceError && <FormErrorMessage>{priceError}</FormErrorMessage>}
                    {!priceError && duoBeneficiaryPriceError && (
                        <FormErrorMessage>{duoBeneficiaryPriceError}</FormErrorMessage>
                    )}
                </FormControl>

                <FormControl flex="1" minW="90px" isInvalid={Boolean(currencyError)}>
                    <FormLabel fontSize="xs">{translate('userMembership.variant_currency')}</FormLabel>
                    <Select
                        size="sm"
                        value={draft.currency}
                        onChange={(e) => setDraftField('currency', e.target.value)}
                    >
                        <option value={Currency.EUR}>EUR</option>
                        <option value={Currency.USD}>USD</option>
                    </Select>
                    {currencyError && <FormErrorMessage>{currencyError}</FormErrorMessage>}
                </FormControl>

                <FormControl flex="1" minW="140px">
                    <FormLabel fontSize="xs">{translate('userMembership.variant_payment_mode')}</FormLabel>
                    <Select
                        size="sm"
                        value={draft.paymentMode}
                        onChange={(e) => setDraftField('paymentMode', e.target.value as MembershipVariantPaymentMode)}
                    >
                        <option value={MembershipVariantPaymentMode.RECURRING}>
                            {translate('userMembership.variant_payment_mode_recurring')}
                        </option>
                        <option value={MembershipVariantPaymentMode.SINGLE}>
                            {translate('userMembership.variant_payment_mode_single')}
                        </option>
                    </Select>
                </FormControl>

                <FormControl flex="1" minW="110px" isInvalid={Boolean(commitmentError)}>
                    <FormLabel fontSize="xs">{translate('userMembership.variant_commitment_months')}</FormLabel>
                    <Select
                        size="sm"
                        value={draft.commitmentMonths}
                        onChange={(e) => setDraftField('commitmentMonths', Number(e.target.value))}
                    >
                        {ALLOWED_COMMITMENT_MONTHS.map((months) => (
                            <option key={months} value={months}>
                                {translate('userMembership.variant_commitment_months_option', { count: months })}
                            </option>
                        ))}
                    </Select>
                    {commitmentError && <FormErrorMessage>{commitmentError}</FormErrorMessage>}
                </FormControl>
            </Flex>

            <Flex mt={3} gap={6} alignItems="center" flexWrap="wrap">
                <FormControl display="flex" alignItems="center" width="auto">
                    <Switch
                        id={`variant-default-${editingId}`}
                        size="sm"
                        isChecked={Boolean(draft.isDefault)}
                        onChange={(e) => setDraftField('isDefault', e.target.checked)}
                        mr={2}
                    />
                    <FormLabel htmlFor={`variant-default-${editingId}`} fontSize="xs" mb={0}>
                        {translate('userMembership.variant_is_default')}
                    </FormLabel>
                </FormControl>

                <FormControl display="flex" alignItems="center" width="auto">
                    <Switch
                        id={`variant-duo-${editingId}`}
                        size="sm"
                        isChecked={Boolean(draft.isDuoBeneficiary)}
                        onChange={(e) => setDraftField('isDuoBeneficiary', e.target.checked)}
                        mr={2}
                    />
                    <FormLabel htmlFor={`variant-duo-${editingId}`} fontSize="xs" mb={0}>
                        {translate('userMembership.variant_is_duo_beneficiary')}
                    </FormLabel>
                </FormControl>
            </Flex>
            <Text fontSize="xs" color={subtleColor} mt={1}>
                {draft.isDefault
                    ? translate('userMembership.variant_is_default_hint')
                    : draft.isDuoBeneficiary
                        ? translate('userMembership.variant_is_duo_beneficiary_hint')
                        : ''}
            </Text>

            {/* MYS-125 P4.9 — D6's resolution table, payer side: which
                partner TYPES make this variant the auto-resolved Duo price.
                Mutually exclusive with isDuoBeneficiary (the 0€ display
                half) — disabled + cleared the same way the price input is
                disabled + zeroed above, one Flex up. */}
            <FormControl mt={3} isInvalid={Boolean(duoPartnerTypeKeysError)}>
                <FormLabel fontSize="xs">{translate('userMembership.variant_duo_partner_type_keys')}</FormLabel>
                {/*
                  * A failed `GET organizations/:id/membershipTypes` empties
                  * `partnerTypeOptions`, and this control derives BOTH halves
                  * of itself from that list — the pickable options and, on the
                  * line below, the rendered `value`. So a refused read did not
                  * merely stop the operator choosing a partner type: it showed
                  * a variant that resolves Duo pricing for several partner
                  * types as resolving for none. The draft still carries the
                  * saved keys, so nothing is lost by saving; what was wrong was
                  * the claim on screen, about a rule that decides a price.
                  */}
                {/* `isUnreadable`, not the raw flag: React Query KEEPS `orgMembershipTypes` through
                    a failed background refetch, and this arm REPLACES the control. Reading
                    the flag alone took a working duo-partner-type select
                    away from the operator because a refresh was refused
                    (useGetUserMembershipTypes, staleTime 0 — R132-1). */}
                {isUnreadable(isMembershipTypesError, orgMembershipTypes) ? (
                    <InlineErrorState message={translate('userMembership.error_loading')} />
                ) : (
                    <MultiSelect
                        isMulti
                        size="sm"
                        isDisabled={draft.isDuoBeneficiary}
                        placeholder={translate('userMembership.variant_duo_partner_type_keys_placeholder')}
                        options={partnerTypeOptions}
                        value={partnerTypeOptions.filter((o) => (draft.duoPartnerTypeKeys ?? []).includes(o.value))}
                        onChange={(selected) =>
                            setDraftField(
                                'duoPartnerTypeKeys',
                                selected.map((o) => o.value),
                            )
                        }
                    />
                )}
                {duoPartnerTypeKeysError && <FormErrorMessage>{duoPartnerTypeKeysError}</FormErrorMessage>}
                <Text fontSize="xs" color={subtleColor} mt={1}>
                    {translate('userMembership.variant_duo_partner_type_keys_hint')}
                </Text>
            </FormControl>

            <Flex mt={3} gap={2} justifyContent="flex-end">
                <Button size="sm" variant="ghost" onClick={cancelEdit} isDisabled={isSaving}>
                    {translate('common.cancel')}
                </Button>
                <Button
                    size="sm"
                    colorScheme="brand"
                    onClick={handleSave}
                    isDisabled={!isDraftValid}
                    isLoading={isSaving}
                >
                    {translate('common.save')}
                </Button>
            </Flex>
        </Box>
    );

    return (
        <Box borderTopWidth="1px" pt={4} mt={4}>
            <Flex align="center" justify="space-between" mb={1}>
                <Box>
                    <FormLabel mb={0}>{translate('userMembership.variants_section_title')}</FormLabel>
                    <Text fontSize="xs" color={subtleColor}>
                        {translate('userMembership.variants_section_hint')}
                    </Text>
                </Box>
                {editingId === null && (
                    <Button size="sm" variant="outline" onClick={startAdd}>
                        {translate('userMembership.variants_add')}
                    </Button>
                )}
            </Flex>

            <Stack spacing={2} mt={3}>
                {variants.length === 0 && editingId !== 'new' && (
                    <Text fontSize="sm" color={subtleColor}>
                        {translate('userMembership.variants_empty')}
                    </Text>
                )}

                {variants.map((variant) =>
                    editingId === variant.id ? (
                        <Box key={variant.id}>{renderDraftRow()}</Box>
                    ) : (
                        <Flex
                            key={variant.id}
                            borderWidth="1px"
                            borderRadius="md"
                            p={2}
                            px={3}
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                            flexWrap="wrap"
                            opacity={editingId ? 0.6 : 1}
                        >
                            <Flex alignItems="center" gap={2} flexWrap="wrap">
                                <Text fontWeight="600" fontSize="sm">{variant.label}</Text>
                                <Text fontSize="sm" color={subtleColor}>{formatVariantPrice(variant)}</Text>
                                <Badge colorScheme={variant.paymentMode === MembershipVariantPaymentMode.SINGLE ? 'purple' : 'blue'}>
                                    {variant.paymentMode === MembershipVariantPaymentMode.SINGLE
                                        ? translate('userMembership.variant_payment_mode_single')
                                        : translate('userMembership.variant_payment_mode_recurring')}
                                </Badge>
                                <Badge>{translate('userMembership.variant_commitment_months_option', { count: variant.commitmentMonths })}</Badge>
                                {variant.isDefault && <Badge colorScheme="green">{translate('common.default')}</Badge>}
                                {variant.isDuoBeneficiary && <Badge colorScheme="orange">{translate('userMembership.variant_is_duo_beneficiary')}</Badge>}
                                {Boolean(variant.duoPartnerTypeKeys?.length) && (
                                    <Badge colorScheme="orange">
                                        {translate('userMembership.variant_duo_partner_type_keys_badge', {
                                            keys: (variant.duoPartnerTypeKeys ?? []).join(', '),
                                        })}
                                    </Badge>
                                )}
                            </Flex>
                            <Flex gap={1}>
                                <IconButton
                                    aria-label={translate('common.edit')}
                                    icon={<MdEdit />}
                                    size="sm"
                                    variant="ghost"
                                    isDisabled={editingId !== null}
                                    onClick={() => startEdit(variant)}
                                />
                                <IconButton
                                    aria-label={translate('common.delete')}
                                    icon={<MdDelete />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    isDisabled={editingId !== null}
                                    onClick={() => setPendingDeleteId(variant.id)}
                                />
                            </Flex>
                        </Flex>
                    ),
                )}

                {editingId === 'new' && renderDraftRow()}
            </Stack>

            <ConfirmDialog
                isOpen={Boolean(pendingDeleteId)}
                onClose={() => setPendingDeleteId(null)}
                onConfirm={handleDelete}
                title={translate('userMembership.variant_delete_confirm_title')}
                body={translate('userMembership.variant_delete_confirm_body')}
                confirmLabel={translate('common.delete')}
                cancelLabel={translate('common.cancel')}
                isConfirming={isDeleting}
            />
        </Box>
    );
}
